import { useState, useEffect } from 'react';
import styled from '@emotion/styled';

import { Grid, Card, CardHeader, CardContent, Divider, CardActions, Button, IconButton } from '@mui/material'

import { CardBlockContainer, CardBlock, CardBlockHeader, CardSpacer } from '../../../utils/utilities/CardComponents';

import { formatPhoneNumber, formatCurrency, formatGenericDateTime } from '../../../utils/util';

import { displayLabelWig } from '../../../utils/displayLabels';

import SellRoundedIcon from '@mui/icons-material/SellRounded';
import PersonPinRoundedIcon from '@mui/icons-material/PersonPinRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import NewReleasesRoundedIcon from '@mui/icons-material/NewReleasesRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';

import { BasicFlex, LabelUpper } from '../../../utils/Common';

import SimpleChip from '../../../utils/utilities/SimpleChip';
import MultiStatusChip from '../../../utils/utilities/MultiStatusChip';

import AutoCompleteMulti from '../../../utils/MuiFields/MuiAutocompleteMulti';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import useSnackBarStore from '../../../store/snackbar';

import { getWigsToAssign, assignWig, unlinkWig } from '../apiService';

import handleErrors from '../../../utils/errorHandler';

const Details = styled.p`
  font-size: 1.6rem;
  /* font-weight: 500; */
  color: ${props => props.theme.colors.mildText};
  line-height: 1.6;
  min-height: 6rem;
  max-width: 38rem;
  margin-top: .2rem;
  font-style: italic;
`

const WigOrderCard = ({ order = {} }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectWig, setSelectWig] = useState(false); // show wig selector
  const [selectedWig, setSelectedWig] = useState(null); // selected wig id

  const [selectedWigInfo, setSelectedWigInfo] = useState({}); // selected wig object

  const [serverOptions, setServerOptions] = useState([]);

  const queryClient = useQueryClient();

  const showSnackbar = useSnackBarStore(state => state.showSnackbar);

  let status = order.fulfillment_status;
  if (order.inventory_status === 'sold') {
    status = 'fulfilled';
  }

  const { data: wigs } = useQuery({
    queryKey: ['inventory', 'inventory_item', order?.inventory_item_id],
    queryFn: () => getWigsToAssign(order?.inventory_item_id || null),
    enabled: !!selectWig
  })

  useEffect(() => {
    if (wigs?.length) {
      setServerOptions(wigs)
    }
  }, [wigs])


  const handleSelectWig = (wig_id, info) => {
    setSelectedWig(wig_id);
    setSelectedWigInfo(info);
  }

  const { mutate: assign } = useMutation({
    mutationFn: assignWig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wig_orders'] });
      showSnackbar('Assigned successfully!', 'success');
    },
    onError: (error) => showSnackbar(handleErrors(error), 'error'),
  })

  const { mutate: unlink } = useMutation({
    mutationFn: unlinkWig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wig_orders'] });
      showSnackbar('Removed Assignment successfully!', 'success');
    },
    onError: (error) => showSnackbar(handleErrors(error), 'error'),
  })

  const handleAssign = () => {
    assign({
      wig_order_id: order.wig_order_id,
      info: {
        ...selectedWigInfo,
        customer_id: order.customer_id,
        customer_name: order.customer_name,
        order_type: order.order_type
      }
    })
    setSelectWig(false);
  }

  const handleUnlink = () => {
    unlink({
      wig_order_id: order.wig_order_id,
      info: {
        fulfillment_status: order.inventory_status,
        inventory_id: order.inventory_id,
        order_type: order.order_type
      }
    })
  }

  let assigned = ["assigned", "fulfilled"].includes(status);

  return (
    <Card variant="outlined" sx={{minWidth: "42rem"}}>
      <CardHeader 
        title={<SimpleChip label={order.sku} uc={true} size="small" palette='basic' />} 
        subheader={<BasicFlex justify="flex-start" gap=".5rem"><SellRoundedIcon color="basic" sx={{fontSize: "1.6rem"}} /><div>{order.wig_id || "UNASSIGNED"}</div></BasicFlex>} 
        sx={{py: 1, backgroundColor: !order.is_assigned ? "bg.light" : "success.faint"}} 
        slotProps={{title: {sx: {fontSize: "1.4rem"}}, subheader: {sx: {fontSize: "1.3rem", mt: ".4rem"}}}} 
        action={<IconButton onClick={() => setExpanded(!expanded)}><MoreVertRoundedIcon fontSize="small" color="basic" /></IconButton>}
      />
      <Divider />
      <CardContent sx={{pt: 2}}>
        <CardBlockContainer $gap="1rem">


        {expanded ? <>  <CardBlockHeader>Customer Details</CardBlockHeader>
          <CardBlock header="" icon={<PersonPinRoundedIcon fontSize="small" color="basic" />} info={order.customer_name} spread="2rem" />
          <CardBlock header="" icon={<PhoneIphoneRoundedIcon fontSize="small" color="basic" />} info={formatPhoneNumber(order.customer_phone)} spread="2rem" />
          {/* <CardBlock header="Email" info={order.customer_email} /> */}

          <CardSpacer />
          <CardBlockHeader>Appointment Details</CardBlockHeader>
          <CardBlock header="" icon={<AutoFixHighRoundedIcon fontSize="small" color="basic" />} info={order.appointment_title} spread="2rem" />
          <CardBlock header="" icon={<TodayRoundedIcon fontSize="small" color="basic" />} info={formatGenericDateTime(order.appointment_start_time)} spread="2rem" />
          <CardBlock header="" icon={<SupportAgentRoundedIcon fontSize="small" color="basic" />} info={order.stylist} spread="2rem" />
          <CardSpacer />
        </> : null}

          <CardBlockHeader>Status</CardBlockHeader>
          <CardBlock header="Order Created" info={formatGenericDateTime(order.created_at)} spread="2rem" />
          <CardBlock header="" icon={<NewReleasesRoundedIcon fontSize="small" color="basic" />} info={<MultiStatusChip value={status} values={[{ label: "Pending", value: "pending", status: "warning" }, { label: "Fulfilled", value: "fulfilled", status: "success" }, {label: "Assigned", value: "assigned", status: "success"} ,{ label: "Cancelled", value: "cancelled", status: "error" }]} />} spread="2rem" />

        <CardSpacer />
        </CardBlockContainer>
        
        <LabelUpper $size="1.3rem" $normal={true}>Order Details</LabelUpper>
        <CardSpacer />
        <CardBlockContainer $gap="1rem">
          <CardBlock header="Color" info={order.color || "N/A"} />
          <CardBlock header="Length" info={order.length || "N/A"} />
          <CardBlock header="Size" info={order.cap_size || "M"} />
          {order.material ? <CardBlock header="Wig Type" info={order.material} /> : null}
          {order.hairstyle ? <CardBlock header="Hairstyle" info={order.hairstyle} /> : null}
          {order.coloring ? <CardBlock header="Coloring" info={order.coloring} /> : null}
        </CardBlockContainer>
       {order.details ? <Details>
        {order.details}
       </Details> : null}
      </CardContent>
      <Divider />
      <CardActions sx={{justifyContent: "flex-end"}}>
        {assigned && <Button color="warning" startIcon={<LinkOffRoundedIcon />} onClick={handleUnlink} >Remove Wig Assignment</Button>}
        {!selectWig ? !assigned ? <Button variant="text" color="secondary" disabled={!!order.is_assigned} startIcon={<AssignmentTurnedInRoundedIcon />} onClick={() => setSelectWig(true)}>Assign Wig ID</Button> : null : (
          <BasicFlex gap="1rem" dir="column">
          <AutoCompleteMulti 
            fieldName="wig_id" 
            url="inventory/inventory_item" 
            value={selectedWig} 
            onChange={handleSelectWig} 
            multiple={false} 
            serverOptions={serverOptions}
            displayLabel={displayLabelWig}
          />
          <BasicFlex gap="1rem">
          <Button variant="outlined" color="error" fullWidth onClick={() => setSelectWig(false)} startIcon={<CancelRoundedIcon />} >Cancel</Button>
          <Button variant="contained" color="secondary" fullWidth disabled={!!order.is_assigned} startIcon={<AssignmentTurnedInRoundedIcon />} onClick={handleAssign} >Assign Wig ID</Button>
          </BasicFlex>
          </BasicFlex>
        ) }
      </CardActions>
    </Card>
  )
};

export default WigOrderCard;